import type { PersistedThreadRuntimeSnapshotInput } from "@/stores/thread-runtime"

const THREAD_RUNTIME_VIEWED_STATE_STORAGE_PREFIX = "cord-thread-runtime-viewed-state:"
/**
 * Viewed cursors are tiny, but this set can otherwise grow forever as a user
 * touches more threads over time. Five hundred per viewer scope comfortably
 * covers heavy daily usage while keeping storage scans and cleanup bounded.
 */
const MAX_PERSISTED_VIEWED_THREAD_COUNT = 500

interface PersistedViewedStateEntry {
  lastViewedEventId: number
  updatedAt: number
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined"
}

function normalizeScopeKey(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeSessionId(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function buildThreadRuntimeViewedStateStoragePrefix(scopeKey: string): string {
  return `${THREAD_RUNTIME_VIEWED_STATE_STORAGE_PREFIX}${scopeKey}:`
}

function buildThreadRuntimeViewedStateStorageKey(args: {
  scopeKey: string
  sessionId: string
}): string {
  return `${buildThreadRuntimeViewedStateStoragePrefix(args.scopeKey)}${args.sessionId}`
}

function isPersistedViewedStateStorageKeyForScope(args: {
  scopeKey: string
  key: string | null
}): boolean {
  return typeof args.key === "string" && args.key.startsWith(buildThreadRuntimeViewedStateStoragePrefix(args.scopeKey))
}

function readPersistedViewedStateEntry(storageKey: string): PersistedViewedStateEntry | null {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== "object") return null

    const entry = parsed as Record<string, unknown>
    const lastViewedEventId = normalizePositiveInteger(entry.lastViewedEventId)
    const updatedAt = normalizePositiveInteger(entry.updatedAt)
    if (lastViewedEventId === null || updatedAt === null) return null

    return {
      lastViewedEventId,
      updatedAt,
    }
  } catch {
    return null
  }
}

function listPersistedViewedStateRows(scopeKey: string): Array<{
  storageKey: string
  sessionId: string
  entry: PersistedViewedStateEntry
}> {
  if (!canUseLocalStorage()) return []

  const prefix = buildThreadRuntimeViewedStateStoragePrefix(scopeKey)
  const rows: Array<{
    storageKey: string
    sessionId: string
    entry: PersistedViewedStateEntry
  }> = []

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const storageKey = window.localStorage.key(index)
    if (typeof storageKey !== "string" || !storageKey.startsWith(prefix)) continue

    const sessionId = normalizeSessionId(storageKey.slice(prefix.length))
    if (!sessionId) continue

    const entry = readPersistedViewedStateEntry(storageKey)
    if (!entry) continue

    rows.push({
      storageKey,
      sessionId,
      entry,
    })
  }

  return rows
}

function prunePersistedViewedStateRows(scopeKey: string): void {
  if (!canUseLocalStorage()) return

  const rows = listPersistedViewedStateRows(scopeKey)
  if (rows.length <= MAX_PERSISTED_VIEWED_THREAD_COUNT) return

  const keysToDelete = rows
    .sort((left, right) => right.entry.updatedAt - left.entry.updatedAt)
    .slice(MAX_PERSISTED_VIEWED_THREAD_COUNT)
    .map((row) => row.storageKey)

  for (const storageKey of keysToDelete) {
    try {
      window.localStorage.removeItem(storageKey)
    } catch {
      // Ignore cleanup failures. They only affect boundedness, not correctness.
    }
  }
}

export function isThreadRuntimeViewedStateStorageEventForScope(args: {
  scopeKey: string
  event: StorageEvent
}): boolean {
  return args.event.storageArea === window.localStorage &&
    isPersistedViewedStateStorageKeyForScope({
      scopeKey: args.scopeKey,
      key: args.event.key,
    })
}

export function loadPersistedThreadRuntimeViewedEntries(args: {
  scopeKey: string
}): PersistedThreadRuntimeSnapshotInput[] {
  const normalizedScopeKey = normalizeScopeKey(args.scopeKey)
  if (!normalizedScopeKey) return []

  const rows = listPersistedViewedStateRows(normalizedScopeKey)
    .sort((left, right) => right.entry.updatedAt - left.entry.updatedAt)
  prunePersistedViewedStateRows(normalizedScopeKey)

  return rows.slice(0, MAX_PERSISTED_VIEWED_THREAD_COUNT).map((row) => ({
    sessionId: row.sessionId,
    lastViewedEventId: row.entry.lastViewedEventId,
  }))
}

export function persistThreadRuntimeViewedEntry(args: {
  scopeKey: string | null
  sessionId: string
  viewedEventId: number
  updatedAtMs?: number
}): void {
  const normalizedScopeKey = normalizeScopeKey(args.scopeKey)
  const normalizedSessionId = normalizeSessionId(args.sessionId)
  const normalizedViewedEventId = normalizePositiveInteger(args.viewedEventId)
  const normalizedUpdatedAt = normalizePositiveInteger(args.updatedAtMs ?? Date.now())
  if (
    normalizedScopeKey === null ||
    normalizedSessionId === null ||
    normalizedViewedEventId === null ||
    normalizedUpdatedAt === null
  ) {
    return
  }

  const storageKey = buildThreadRuntimeViewedStateStorageKey({
    scopeKey: normalizedScopeKey,
    sessionId: normalizedSessionId,
  })
  const existingEntry = readPersistedViewedStateEntry(storageKey)
  const nextViewedEventId = Math.max(existingEntry?.lastViewedEventId ?? 0, normalizedViewedEventId)
  const nextUpdatedAt = Math.max(existingEntry?.updatedAt ?? 0, normalizedUpdatedAt)

  if (
    existingEntry?.lastViewedEventId === nextViewedEventId &&
    existingEntry.updatedAt === nextUpdatedAt
  ) {
    return
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      lastViewedEventId: nextViewedEventId,
      updatedAt: nextUpdatedAt,
    } satisfies PersistedViewedStateEntry))
  } catch {
    // Ignore storage quota and serialization errors. The in-memory state still works.
    return
  }

  prunePersistedViewedStateRows(normalizedScopeKey)
}
